/**
 * SOVRANOVA — useChatAutoScroll
 *
 * THE BUG THIS FIXES (19 Aug, David): every chat surface called
 * scrollIntoView on every message update. In a live voice conversation
 * messages update constantly (interim transcripts, streamed replies), so the
 * page "bounced" — trying to scroll up to reread something, the view was
 * yanked back to the bottom on every event and felt stuck.
 *
 * THE RULE: follow the conversation only while the reader is already at the
 * bottom. The moment they scroll up to read, stop following; the moment they
 * come back down, resume. This is how every decent chat app behaves.
 *
 * Usage: useChatAutoScroll(bottomRef, messages) — replaces the old
 * `useEffect(() => bottomRef.current?.scrollIntoView(...), [messages])`.
 */
import { useEffect, useRef } from 'react'

const NEAR_BOTTOM_PX = 80

function findScrollParent(el) {
  let p = el?.parentElement
  while (p && p !== document.body) {
    const oy = getComputedStyle(p).overflowY
    if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) return p
    p = p.parentElement
  }
  return null   // the page itself scrolls
}

export function useChatAutoScroll(bottomRef, dep) {
  // Sticky by default: a fresh conversation starts at the bottom.
  const stickRef = useRef(true)

  // Track whether the reader is at the bottom. Re-armed whenever the bottom
  // marker mounts (the container can change when a view's phase changes).
  useEffect(() => {
    const el = bottomRef.current
    if (!el) return
    const container = findScrollParent(el)
    const measure = () => {
      if (container) {
        stickRef.current =
          container.scrollHeight - container.scrollTop - container.clientHeight < NEAR_BOTTOM_PX
      } else {
        const doc = document.scrollingElement || document.documentElement
        stickRef.current = doc.scrollHeight - window.scrollY - window.innerHeight < NEAR_BOTTOM_PX
      }
    }
    const target = container || window
    target.addEventListener('scroll', measure, { passive: true })
    return () => target.removeEventListener('scroll', measure)
  }, [bottomRef, bottomRef.current])

  // Follow new content ONLY while stuck to the bottom.
  useEffect(() => {
    if (!stickRef.current) return
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [dep])
}
